import { DeviceRegistry } from './audio/deviceRegistry';
import { NotifyReason, SwitchCooldown } from './audio/devicePolicy';
import { DeviceChangeSink, DeviceWatcher } from './audio/deviceWatcher';
import { checkMicrophoneAccess, requestMicrophoneAccess } from './audio/permissionStream';
import { createPlatformQuirks } from './audio/platformQuirks';
import { DeviceLite, LoggerBridge } from './audio/shared';
import { MicAcquireError, MicSource } from './voice/micSource';
import { OutputRouter } from './voice/outputRouter';
import { CallBridge, VoiceCallSession, VoiceCallStartOptions, VoiceCallStartResult, VoiceCallStats } from './voice/voiceCall';

// WebRTC voice calls with the agent. The browser sends its microphone to Azure OpenAI and plays the agent's
// answer; our server creates each call from the SDP offer and runs the conversation. Every method is invoked
// by name from C# (Services/Voice/VoiceCallService.cs and VoiceCall.cs), so names and signatures are an
// interop contract.

// Bridge to the C# VoiceCallService DotNetObjectReference for device hot-plug decisions.
class DeviceChangeBridge implements DeviceChangeSink {
    private ref: any = null;

    constructor(private readonly logger: LoggerBridge) { }

    setRef(ref: any): void {
        if (ref) this.ref = ref;
    }

    async onDeviceChanged(fallbackDeviceId: string | null, reason: NotifyReason): Promise<void> {
        try {
            await this.ref?.invokeMethodAsync('OnDeviceChangedAsync', fallbackDeviceId, reason);
        } catch (e) {
            await this.logger.warning('Failed to invoke OnDeviceChangedAsync', { error: String(e), reason });
        }
    }
}

//#region Composition root
const logger = new LoggerBridge();
const registry = new DeviceRegistry();
const cooldown = new SwitchCooldown();
const quirks = createPlatformQuirks(logger, registry, {
    get state() { return output.state; },
    get sinkId() { return output.sinkId; }
});
const output = new OutputRouter(logger, registry, quirks);
const mic = new MicSource(logger, quirks, cooldown);
const watcher = new DeviceWatcher(
    registry, new DeviceChangeBridge(logger), logger, quirks, mic, cooldown, output,
    () => output.route(mic.currentMicId));
mic.setTrackEndedCallback(() => watcher.requestRecheck());

const calls = new Map<string, VoiceCallSession>();

// Leaving the page ends every call at once: pc.close() hands the call back to Azure, and the server's
// sideband notices within seconds. There is no time left to tell the server first.
window.addEventListener('pagehide', () => {
    for (const call of calls.values()) void call.stopForPageHide();
    calls.clear();
});
//#endregion

class VoiceCall {
    public static setLogger(loggerRef: any): void {
        logger.setRef(loggerRef);
    }

    //#region Calls
    public static async start(callRef: any, callId: string, options: VoiceCallStartOptions): Promise<VoiceCallStartResult> {
        await calls.get(callId)?.stop();
        const call = new VoiceCallSession(callId, new CallBridge(callRef), mic, output, logger);
        calls.set(callId, call);
        const result = await call.start(options);
        if (result.offerSdp === null && calls.get(callId) === call) calls.delete(callId);
        return result;
    }

    public static async applyAnswer(callId: string, answerSdp: string): Promise<boolean> {
        return (await calls.get(callId)?.applyAnswer(answerSdp)) ?? false;
    }

    public static setMuted(callId: string, muted: boolean): void {
        calls.get(callId)?.setMuted(muted);
    }

    public static setOutputMuted(callId: string, muted: boolean): void {
        calls.get(callId)?.setOutputMuted(muted);
    }

    public static async resumePlayback(callId: string): Promise<void> {
        await calls.get(callId)?.resumePlayback();
    }

    public static async stop(callId: string): Promise<void> {
        const call = calls.get(callId);
        calls.delete(callId);
        await call?.stop();
    }

    public static async getStats(callId: string): Promise<VoiceCallStats | null> {
        return (await calls.get(callId)?.getStats()) ?? null;
    }

    // For support and the end-to-end harness: everything about the calls except SDP and transcripts.
    public static debugSnapshot(): object {
        return {
            micActive: mic.isActive,
            micId: mic.currentMicId,
            micLeases: mic.leaseCount,
            sinkId: output.sinkId,
            outputState: output.state,
            calls: [...calls.values()].map(call => call.snapshot())
        };
    }
    //#endregion

    //#region Devices
    // Moves every call to another microphone without renegotiating. Returns an error code, or null on success.
    public static async switchMicrophone(micId: string | null): Promise<string | null> {
        try {
            await mic.switchTo(micId ?? null);
            await output.route(mic.currentMicId, true);
            return null;
        } catch (e) {
            return e instanceof MicAcquireError ? e.code : 'mic-unavailable';
        }
    }

    public static setOutputDevice(deviceId: string | null): Promise<boolean> {
        return output.setSink(deviceId ?? null);
    }

    public static async getAudioDevices(): Promise<DeviceLite[]> {
        const snapshot = await registry.enumerate({ ensureLabels: true });
        return [...snapshot.inputs, ...snapshot.outputs];
    }

    public static async getMicList(): Promise<DeviceLite[]> {
        return (await VoiceCall.getAudioDevices()).filter(d => d.kind === 'audioinput');
    }

    public static checkMicrophoneAccess(): Promise<boolean> {
        return checkMicrophoneAccess(registry, logger);
    }

    // From a click: the granted stream is kept 30 s, so the first call uses it without a second prompt.
    public static requestMicrophoneAccess(): Promise<boolean> {
        return requestMicrophoneAccess(logger);
    }

    public static async registerDeviceChangeListener(serviceRef: any): Promise<void> {
        await watcher.register(serviceRef);
    }

    public static unregisterDeviceChangeListener(): void {
        watcher.unregister();
    }

    public static getDiagnosticInfo(): object {
        return {
            sinkId: output.sinkId,
            outputState: output.state,
            micId: mic.currentMicId,
            iosAudioCategory: quirks.diagnosticCategory()
        };
    }
    //#endregion
}

(window as any).VoiceCall = VoiceCall;
